import type { BrandSanctionLevel, SanctionDto } from '../api/payment/brand-sanction.api';
import { formatCa } from './individual-order';
import { parseServerDate } from './server-date';

// Xử phạt người đặt đơn (spec 2026-10-09-brand-orderer-sanctions §5). Hàm thuần dùng chung cho phía người đặt
// và phía admin. Không bao giờ hiện mã cấp thô (Suspend7…) ra giao diện.

export const ORDERER_ERROR_CODE = {
  sanctioned: 'ORDERER_SANCTIONED',
  blacklisted: 'ORDERER_BLACKLISTED',
} as const;

/** Sự kiện window để mọi nơi đang hiện trạng thái khoá tải lại (RB7). */
export const SANCTION_CHANGED_EVENT = 'cab:orderer-sanction-changed';

export const notifySanctionChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SANCTION_CHANGED_EVENT));
};

export const SANCTION_LEVELS: BrandSanctionLevel[] = ['Warning', 'Suspend7', 'Suspend30', 'Permanent'];

const LEVEL_LABEL: Record<BrandSanctionLevel, string> = {
  Warning: 'Cảnh cáo',
  Suspend7: 'Khoá tạo việc 7 ngày',
  Suspend30: 'Khoá tạo việc 30 ngày',
  Permanent: 'Khoá vĩnh viễn',
};

export const sanctionLevelLabel = (level: string | null | undefined): string =>
  (level && LEVEL_LABEL[level as BrandSanctionLevel]) || 'Xử phạt';

/** Thứ tự nặng nhẹ; mã lạ = -1. */
export const levelRank = (level: string | null | undefined): number => SANCTION_LEVELS.indexOf(level as BrandSanctionLevel);

export const isLowerLevel = (chosen: string, suggested: string): boolean => levelRank(chosen) < levelRank(suggested);

export const SANCTION_LOCK_HINT = 'Tạm thời chưa tạo được việc mới trong thời gian bị khoá.';

const pad = (n: number) => String(n).padStart(2, '0');

// Giờ Việt Nam cố định UTC+7 (RB3), không phụ thuộc múi giờ của máy người xem hay máy chạy test.
const vnParts = (iso: string) => {
  // Payment trả giờ UTC nhưng có thể không kèm 'Z': parseServerDate coi chuỗi thiếu múi giờ là UTC.
  const t = parseServerDate(iso)?.getTime();
  if (t == null) return null;
  const d = new Date(t + 7 * 3600 * 1000);
  return { d: pad(d.getUTCDate()), m: pad(d.getUTCMonth() + 1), y: d.getUTCFullYear(), h: pad(d.getUTCHours()), mi: pad(d.getUTCMinutes()) };
};

export const formatVnTime = (iso: string | null | undefined): string => {
  const p = iso ? vnParts(iso) : null;
  return p ? `${p.d}/${p.m}/${p.y} ${p.h}:${p.mi}` : '';
};

export const formatVnDate = (iso: string | null | undefined): string => {
  const p = iso ? vnParts(iso) : null;
  return p ? `${p.d}/${p.m}/${p.y}` : '';
};

/** Lần phạt đang chặn tạo việc: chưa gỡ, không phải cảnh cáo, vĩnh viễn hoặc còn hạn. */
export const isBlockingSanction = (s: SanctionDto | null | undefined, nowMs: number): boolean => {
  if (!s || s.revokedAt || s.level === 'Warning') return false;
  if (s.level === 'Permanent') return true;
  const ends = parseServerDate(s.endsAt)?.getTime();
  return ends != null && ends > nowMs;
};

export const sanctionBannerText = (s: SanctionDto): string =>
  s.level === 'Permanent'
    ? `Tài khoản đã bị khoá vĩnh viễn khỏi sàn hợp tác. Lý do: ${s.reason}`
    : `Tài khoản đang bị tạm khoá tạo việc đến ${formatVnTime(s.endsAt)}. Lý do: ${s.reason}`;

/** Cột "Thời hạn" trong lịch sử. */
export const sanctionWindowLabel = (s: SanctionDto): string => {
  if (s.revokedAt) return `Đã gỡ ngày ${formatVnDate(s.revokedAt)}`;
  if (s.level === 'Warning') return 'Không khoá';
  if (s.level === 'Permanent') return 'Không thời hạn';
  return s.endsAt ? `Đến ${formatVnTime(s.endsAt)}` : '';
};

/** Cột "Bồi thường đã trừ": cá nhân tính bằng Ca nạp, doanh nghiệp bằng VND. */
export const compensationDeductedLabel = (s: SanctionDto): string => {
  if (s.compensatedCa != null && s.compensatedCa > 0) return `${formatCa(s.compensatedCa)} Ca nạp`;
  if (s.compensatedVnd > 0) return `${formatCa(s.compensatedVnd)}đ`;
  return 'Không';
};

export const isOrdererBlockCode = (code: string | null | undefined): boolean =>
  code === ORDERER_ERROR_CODE.sanctioned || code === ORDERER_ERROR_CODE.blacklisted;
